import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import axios from 'axios';
import { ArrowLeft, Globe, Bell, User, LogOut, Save, CreditCard, Camera, Upload, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Settings = () => {
  const { user, token, logout, updateUser } = useAuth();
  const { t, language, changeLanguage, languageLabels } = useLanguage();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [loading, setLoading] = useState(false);
  const [savingBank, setSavingBank] = useState(false);
  const [savingPhoto, setSavingPhoto] = useState(false);
  
  const [formData, setFormData] = useState({
    name: user?.name || '',
    language: user?.language || language,
    company_name: user?.company_name || '',
    vat_number: user?.vat_number || '',
    age: user?.age || '',
    gender: user?.gender || '',
    residence_city: user?.residence_city || '',
    paypal_address: user?.paypal_address || '',
    phone_number: user?.phone_number || ''
  });
  
  const [bankData, setBankData] = useState({
    iban: user?.iban || '',
    bank_name: user?.bank_name || '',
    account_holder: user?.account_holder || ''
  });
  
  const [notifications, setNotifications] = useState({
    donations: true, messages: true, updates: true
  });

  const handleSaveProfile = async () => {
    setLoading(true);
    try {
      const response = await axios.put(
        `${API_URL}/api/users/profile`,
        { 
          name: formData.name, language: formData.language,
          company_name: formData.company_name, vat_number: formData.vat_number,
          age: formData.age ? parseInt(formData.age) : null,
          gender: formData.gender || null,
          residence_city: formData.residence_city || null,
          paypal_address: formData.paypal_address || null,
          phone_number: formData.phone_number || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      updateUser(response.data);
      changeLanguage(formData.language);
      toast.success(t('success'));
    } catch { toast.error(t('error')); } finally { setLoading(false); }
  };

  const handleSaveBankDetails = async () => {
    setSavingBank(true);
    try {
      const response = await axios.put(`${API_URL}/api/users/bank-details`, bankData,
        { headers: { Authorization: `Bearer ${token}` } });
      updateUser(response.data);
      toast.success(t('success'));
    } catch (error) { toast.error(error.response?.data?.detail || t('error')); }
    finally { setSavingBank(false); }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Max 5MB'); return; }
    setSavingPhoto(true);
    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        const response = await axios.post(`${API_URL}/api/users/profile-photo`,
          { photo: reader.result }, { headers: { Authorization: `Bearer ${token}` } });
        updateUser({ picture: response.data.picture });
        toast.success(t('success'));
      } catch { toast.error(t('error')); } finally { setSavingPhoto(false); }
    };
    reader.readAsDataURL(file);
  };

  const genderOptions = [
    { value: 'male', label: t('male') },
    { value: 'female', label: t('female') },
    { value: 'non_binary', label: t('nonBinary') },
    { value: 'prefer_not_say', label: t('preferNotSay') }
  ];

  return (
    <div className="min-h-screen bg-stone-100 overflow-y-auto">
      <Navbar />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-20">
        <button onClick={() => navigate(-1)} className="inline-flex items-center text-stone-600 hover:text-stone-900 mb-6">
          <ArrowLeft className="h-5 w-5 mr-2" />{t('back')}
        </button>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="font-heading text-3xl font-bold text-stone-900 mb-8">{t('settings')}</h1>

          <Tabs defaultValue="profile" className="w-full">
            <TabsList className="w-full justify-start bg-white rounded-xl p-1 mb-6 overflow-x-auto flex-nowrap">
              <TabsTrigger value="profile" className="rounded-lg data-[state=active]:bg-amber-500 data-[state=active]:text-white whitespace-nowrap">
                <User className="h-4 w-4 mr-2" />{t('profile')}
              </TabsTrigger>
              <TabsTrigger value="bank" className="rounded-lg data-[state=active]:bg-amber-500 data-[state=active]:text-white whitespace-nowrap">
                <CreditCard className="h-4 w-4 mr-2" />{t('paymentMethod')}
              </TabsTrigger>
              <TabsTrigger value="notifications" className="rounded-lg data-[state=active]:bg-amber-500 data-[state=active]:text-white whitespace-nowrap">
                <Bell className="h-4 w-4 mr-2" />{t('notifications')}
              </TabsTrigger>
            </TabsList>

            {/* Profile Tab */}
            <TabsContent value="profile">
              {/* Photo */}
              <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-6 mb-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                    <Camera className="h-5 w-5 text-amber-700" />
                  </div>
                  <h2 className="font-heading text-xl font-semibold text-stone-900">
                    {t('profile')}
                  </h2>
                </div>
                <div className="flex items-center gap-6">
                  <Avatar className="h-24 w-24 ring-4 ring-stone-100">
                    <AvatarImage src={user?.picture || `https://api.dicebear.com/9.x/lorelei/svg?seed=${user?.user_id}`} />
                    <AvatarFallback className="text-2xl">{user?.name?.[0]}</AvatarFallback>
                  </Avatar>
                  <div>
                    <input type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" ref={fileInputRef} />
                    <Button data-testid="upload-photo-btn" variant="outline" className="rounded-xl"
                      onClick={() => fileInputRef.current?.click()} disabled={savingPhoto}>
                      {savingPhoto ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-amber-700 mr-2"></div>
                        : <Upload className="h-4 w-4 mr-2" />}
                      {t('uploadImages')}
                    </Button>
                    <p className="text-sm text-stone-500 mt-2">JPG, PNG. Max 5MB</p>
                  </div>
                </div>
              </div>

              {/* Personal Info */}
              <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-6 mb-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                    <User className="h-5 w-5 text-amber-700" />
                  </div>
                  <h2 className="font-heading text-xl font-semibold text-stone-900">
                    {t('editProfile')}
                  </h2>
                </div>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">{t('name')}</Label>
                    <Input id="name" data-testid="settings-name-input" value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="h-12 rounded-xl border-stone-200" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">{t('email')}</Label>
                    <Input id="email" value={user?.email || ''} disabled className="h-12 rounded-xl border-stone-200 bg-stone-50" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="age">{t('age')}</Label>
                      <Input id="age" data-testid="settings-age-input" type="number" min="13" max="120"
                        placeholder="25" value={formData.age}
                        onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                        className="h-12 rounded-xl border-stone-200" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="gender">{t('gender')}</Label>
                      <Select value={formData.gender} onValueChange={(v) => setFormData({ ...formData, gender: v })}>
                        <SelectTrigger data-testid="settings-gender-select" className="h-12 rounded-xl border-stone-200">
                          <SelectValue placeholder={t('preferNotSay')} />
                        </SelectTrigger>
                        <SelectContent>
                          {genderOptions.map(opt => (
                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="residence_city">{t('city')}</Label>
                    <Input id="residence_city" data-testid="settings-city-input"
                      placeholder={language === 'it' ? 'es. Milano' : 'e.g. Berlin'}
                      value={formData.residence_city}
                      onChange={(e) => setFormData({ ...formData, residence_city: e.target.value })}
                      className="h-12 rounded-xl border-stone-200" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="paypal_address">PayPal</Label>
                    <Input id="paypal_address" data-testid="settings-paypal-input"
                      placeholder="https://paypal.me/tuonome"
                      value={formData.paypal_address}
                      onChange={(e) => setFormData({ ...formData, paypal_address: e.target.value })}
                      className="h-12 rounded-xl border-stone-200" />
                    <p className="text-xs text-stone-400">
                      {language === 'it' ? 'Visibile nel tuo profilo pubblico per ricevere donazioni dirette' : 'Visible on your public profile for direct donations'}
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone_number">{language === 'it' ? 'Numero di telefono' : 'Phone number'}</Label>
                    <Input id="phone_number" data-testid="settings-phone-input"
                      placeholder="+39 333 1234567"
                      value={formData.phone_number}
                      onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                      className="h-12 rounded-xl border-stone-200" />
                  </div>

                  {user?.user_type === 'company' && (
                    <>
                      <div className="space-y-2">
                        <Label>{language === 'it' ? 'Nome Azienda' : 'Company Name'}</Label>
                        <Input data-testid="settings-company-name-input" value={formData.company_name}
                          onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                          className="h-12 rounded-xl border-stone-200" />
                      </div>
                      <div className="space-y-2">
                        <Label>{language === 'it' ? 'Partita IVA' : 'VAT Number'}</Label>
                        <Input data-testid="settings-vat-input" value={formData.vat_number}
                          onChange={(e) => setFormData({ ...formData, vat_number: e.target.value })}
                          className="h-12 rounded-xl border-stone-200" />
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Language */}
              <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-6 mb-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
                    <Globe className="h-5 w-5 text-indigo-700" />
                  </div>
                  <h2 className="font-heading text-xl font-semibold text-stone-900">
                    {language === 'it' ? 'Lingua' : 'Language'}
                  </h2>
                </div>
                <Select value={formData.language} onValueChange={(v) => setFormData({ ...formData, language: v })}>
                  <SelectTrigger data-testid="language-select" className="h-12 rounded-xl border-stone-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(languageLabels).map(([code, info]) => (
                      <SelectItem key={code} value={code}>{info.flag} {info.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Button data-testid="save-profile-btn" onClick={handleSaveProfile} disabled={loading}
                className="w-full h-12 bg-amber-500 hover:bg-amber-600 text-white rounded-xl font-semibold">
                <Save className="h-4 w-4 mr-2" />{loading ? t('loading') : t('save')}
              </Button>
            </TabsContent>

            {/* Bank Tab */}
            <TabsContent value="bank">
              <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-6 mb-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                    <CreditCard className="h-5 w-5 text-amber-700" />
                  </div>
                  <div>
                    <h2 className="font-heading text-xl font-semibold text-stone-900">IBAN</h2>
                    <p className="text-sm text-stone-500">
                      {language === 'it' ? 'Per ricevere bonifici diretti' : 'To receive direct transfers'}
                    </p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="iban">IBAN</Label>
                    <Input id="iban" data-testid="iban-input" placeholder="IT60X0542811101000000123456"
                      value={bankData.iban} onChange={(e) => setBankData({ ...bankData, iban: e.target.value.toUpperCase() })}
                      className="h-12 rounded-xl border-stone-200 font-mono" />
                  </div>
                  <div className="space-y-2">
                    <Label>{language === 'it' ? 'Nome Banca' : 'Bank Name'}</Label>
                    <Input data-testid="bank-name-input" value={bankData.bank_name}
                      onChange={(e) => setBankData({ ...bankData, bank_name: e.target.value })}
                      className="h-12 rounded-xl border-stone-200" />
                  </div>
                  <div className="space-y-2">
                    <Label>{language === 'it' ? 'Intestatario' : 'Account Holder'}</Label>
                    <Input data-testid="account-holder-input" value={bankData.account_holder}
                      onChange={(e) => setBankData({ ...bankData, account_holder: e.target.value })}
                      className="h-12 rounded-xl border-stone-200" />
                  </div>
                </div>
              </div>
              <Button data-testid="save-bank-btn" onClick={handleSaveBankDetails} disabled={savingBank}
                className="w-full h-12 bg-amber-500 hover:bg-amber-600 text-white rounded-xl font-semibold">
                <Save className="h-4 w-4 mr-2" />{savingBank ? t('loading') : t('save')}
              </Button>
            </TabsContent>

            {/* Notifications Tab */}
            <TabsContent value="notifications">
              <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-6 mb-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
                    <Bell className="h-5 w-5 text-red-600" />
                  </div>
                  <h2 className="font-heading text-xl font-semibold text-stone-900">{t('notifications')}</h2>
                </div>
                <div className="space-y-4">
                  {[
                    { key: 'donations', label: t('donations') },
                    { key: 'messages', label: t('conversations') },
                    { key: 'updates', label: language === 'it' ? 'Aggiornamenti' : 'Updates' }
                  ].map(item => (
                    <div key={item.key} className="flex items-center justify-between">
                      <p className="font-medium text-stone-900">{item.label}</p>
                      <Switch checked={notifications[item.key]}
                        onCheckedChange={(checked) => setNotifications({ ...notifications, [item.key]: checked })} />
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>
          </Tabs>

          <div className="mt-8 pt-6 border-t border-stone-200 space-y-3">
            <Button data-testid="logout-btn" variant="outline" onClick={() => { logout(); navigate('/'); }}
              className="w-full h-12 rounded-xl border-red-200 text-red-600 hover:bg-red-50">
              <LogOut className="h-4 w-4 mr-2" />{t('logout')}
            </Button>
            <Button data-testid="delete-account-btn" variant="outline"
              onClick={async () => {
                const confirm1 = window.confirm(language === 'it' ? 'Sei sicuro di voler eliminare il tuo account? Questa azione è irreversibile.' : 'Are you sure you want to delete your account? This action is irreversible.');
                if (!confirm1) return;
                const confirm2 = window.confirm(language === 'it' ? 'ULTIMA CONFERMA: Tutti i tuoi dati, post, messaggi e donazioni verranno eliminati permanentemente. Procedere?' : 'FINAL CONFIRMATION: All your data, posts, messages and donations will be permanently deleted. Continue?');
                if (!confirm2) return;
                try {
                  await axios.delete(`${API_URL}/api/auth/account`, { headers: { Authorization: `Bearer ${token}` } });
                  toast.success(language === 'it' ? 'Account eliminato' : 'Account deleted');
                  logout();
                  navigate('/');
                } catch { toast.error('Error'); }
              }}
              className="w-full h-12 rounded-xl border-red-300 text-red-700 hover:bg-red-50 font-medium">
              <Trash2 className="h-4 w-4 mr-2" />{language === 'it' ? 'Elimina account' : 'Delete account'}
            </Button>
          </div>
        </motion.div>
      </main>
    </div>
  );
};

export default Settings;
